import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Coins, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public "Top Earners This Month" ticker — landing-page social proof.
 *
 * Pulls /api/public/top-earners (60s cached server-side, no auth) and renders a
 * scrolling marquee of real creator earnings using the format "First L. — N XEA".
 * Hides itself entirely when there's no data so the landing page stays clean
 * during quiet months.
 */
export const TopEarnersTicker = () => {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/top-earners`);
        if (!alive) return;
        setItems(r.data.items || []);
        setMonth(r.data.month || "");
      } catch (e) {
        // landing page never errors — just render nothing
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!items.length) return null;

  // Duplicate the list so the marquee loops seamlessly
  const loop = [...items, ...items];

  return (
    <section
      className="bg-gradient-to-r from-amber-50 via-coral-500/5 to-amber-50 border-y border-amber-300/40 py-5 overflow-hidden"
      data-testid="top-earners-ticker"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-amber-400 to-coral-500 text-white flex items-center justify-center shadow-md shrink-0">
            <Trophy className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700">
              Top creator earnings · {month}
            </p>
            <p className="text-sm font-bold text-ink-950 leading-tight">
              Real XEA tokens earned by community creators this month
            </p>
          </div>
          <span
            className="hidden sm:inline-flex ml-auto items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-amber-300/60 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700"
            data-testid="top-earners-live-pill"
          >
            <Sparkles className="w-3 h-3" />
            Live
          </span>
        </div>

        <div className="relative">
          <div
            className="flex gap-3 marquee-content"
            style={{ animation: "marquee 38s linear infinite" }}
          >
            {loop.map((e, i) => (
              <div
                key={`${e.rank}-${i}`}
                data-testid={`top-earner-${e.rank}`}
                className="shrink-0 inline-flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-white border border-amber-300/60 shadow-sm"
              >
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-coral-500 text-white text-xs font-black flex items-center justify-center shrink-0">
                  #{e.rank}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-ink-950 leading-tight whitespace-nowrap">
                    {e.name}
                  </p>
                  <p className="text-[11px] text-ink-500 leading-tight whitespace-nowrap inline-flex items-center gap-1">
                    <Coins className="w-3 h-3 text-amber-600" />
                    <strong className="text-amber-700">{e.earned.toLocaleString()}</strong> XEA earned · {e.tips_received} tip{e.tips_received === 1 ? "" : "s"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        section[data-testid="top-earners-ticker"]:hover .marquee-content {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export default TopEarnersTicker;
